import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const NONE = "__none__";

function defaultValue(f) {
  if (f.type === "bool") return f.name === "attivo";
  return "";
}

export default function EntityForm({ open, onOpenChange, meta, initial, onSaved }) {
  const [values, setValues] = useState({});
  const [refOptions, setRefOptions] = useState({});
  const [saving, setSaving] = useState(false);

  const fields = meta?.fields || [];

  useEffect(() => {
    if (!open) return;
    const init = {};
    fields.forEach((f) => {
      init[f.name] = initial?.[f.name] ?? defaultValue(f);
    });
    setValues(init);
    // fetch ref options
    const refs = [...new Set(fields.filter((f) => f.type === "ref").map((f) => f.ref))];
    refs.forEach((ref) => {
      api
        .get(`/${ref}/options`)
        .then((r) => setRefOptions((prev) => ({ ...prev, [ref]: r.data })))
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const sections = useMemo(() => {
    const map = new Map();
    const tipoMezzo = values.tipo_veicolo;
    fields.forEach((f) => {
      if (f.tipi_mezzo?.length && tipoMezzo && !f.tipi_mezzo.includes(tipoMezzo)) return;
      const key = f.section || "Generale";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    });
    return [...map.entries()].filter(([, fs]) => fs.length > 0);
  }, [fields, values.tipo_veicolo]);

  const setVal = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    fields.forEach((f) => {
      let v = values[f.name];
      if (f.type === "number" || f.type === "decimal") {
        v = v === "" || v === null ? null : Number(v);
      }
      if (f.type === "ref" && (v === "" || v === NONE)) v = null;
      payload[f.name] = v;
    });
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/${meta.entity}/${initial.id}`, payload);
        toast.success(`${meta.singular} aggiornato`);
      } else {
        await api.post(`/${meta.entity}`, payload);
        toast.success(`${meta.singular} creato`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const v = values[f.name];
    const common = { id: f.name, "data-testid": `field-${f.name}` };
    if (f.type === "bool") {
      return (
        <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 h-10">
          <Label htmlFor={f.name} className="cursor-pointer">
            {f.label}
          </Label>
          <Switch
            checked={!!v}
            onCheckedChange={(c) => setVal(f.name, c)}
            data-testid={`field-${f.name}`}
          />
        </div>
      );
    }
    return (
      <div className={f.type === "textarea" ? "sm:col-span-2" : ""}>
        <Label htmlFor={f.name} className="text-xs">
          {f.label}
          {f.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <div className="mt-1">
          {f.type === "textarea" ? (
            <Textarea {...common} value={v ?? ""} onChange={(e) => setVal(f.name, e.target.value)} rows={2} />
          ) : f.type === "select" ? (
            <Select value={v ? String(v) : ""} onValueChange={(val) => setVal(f.name, val)}>
              <SelectTrigger {...common}>
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent>
                {f.options.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : f.type === "ref" ? (
            <Select
              value={v ? String(v) : NONE}
              onValueChange={(val) => setVal(f.name, val === NONE ? "" : val)}
            >
              <SelectTrigger {...common}>
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— Nessuno —</SelectItem>
                {(refOptions[f.ref] || []).map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              {...common}
              type={f.type === "number" || f.type === "decimal" ? "number" : f.type === "date" ? "date" : "text"}
              step={f.type === "decimal" ? "any" : undefined}
              value={v ?? ""}
              onChange={(e) => setVal(f.name, e.target.value)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="entity-form">
        <DialogHeader>
          <DialogTitle className="font-head">
            {initial?.id ? `Modifica ${meta?.singular}` : `Nuovo ${meta?.singular}`}
          </DialogTitle>
          <DialogDescription>
            Compila i campi. I campi contrassegnati con * sono obbligatori.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {sections.map(([sname, sfields]) => (
            <div key={sname}>
              {sections.length > 1 && (
                <div className="text-xs font-semibold uppercase tracking-wider text-sky-700 mb-2 border-b border-slate-100 pb-1">
                  {sname}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sfields.map((f) => (
                  <div key={f.name} className="contents">
                    {renderField(f)}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving} data-testid="entity-form-submit">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
